// src/pages/People/DetailInfoPage.tsx
import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/ru";

import type {PersonModelInterface} from "@/models/personModel";
import {usePeople} from "@/context/PeopleContext";
import DetailInfoSkeleton from "./DetailInfoSkeleton";

// old edit forms (оставляем как есть)
import PersonInfoForms from "../../components/Forms/PersonInfo/PersonInfoForms";
import PhoneInfoForms from "../../components/Forms/PhoneInfo/PhoneInfoForms";
import LoanCalculationForms from "../../components/Forms/LoanCalculation/LoanCalculationForms";

// shadcn ui
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Separator} from "@/components/ui/separator";

// icons
import {ArrowLeft, Edit3, Save, X} from "lucide-react";

// новые карточки View режима (ты их создашь, я использую как пример пути)
import {PersonalInfoCard} from "@/components/detailClients/PersonalInfoCard";
import {PhoneInfoCard} from "@/components/detailClients/PhoneInfoCard";
import {CreditInfoCard} from "@/components/detailClients/CreditInfoCard";
import {CommentsCard} from "@/components/detailClients/Comments";
// import {CommentsCard} from "@/components/client-detail/CommentsCard";

function buildFullName(data: PersonModelInterface) {
    const first = data?.detailInfo?.firstName || "";
    const second = data?.detailInfo?.secondName || "";
    const last = data?.detailInfo?.lastName || "";
    return `${first} ${second}${last ? ` ${last}` : ""}`.trim();
}

const DetailInfoPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {getPersonById, updatePerson} = usePeople();

    const [currentPerson, setCurrentPerson] = useState<PersonModelInterface | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // режимы: просмотр или редактирование
    const [isEditing, setIsEditing] = useState(false);

    const methods = useForm<PersonModelInterface>({
        defaultValues: {} as PersonModelInterface,
        mode: "onChange",
    });

    const {reset, handleSubmit} = methods;

    useEffect(() => {
        if (!id) return;

        setIsLoading(true);
        getPersonById(id)
            .then((person) => {
                if (person) {
                    setCurrentPerson(person);
                    reset(person);
                } else {
                    setCurrentPerson(null);
                }
            })
            .catch(() => setCurrentPerson(null))
            .finally(() => setIsLoading(false));
    }, [id, getPersonById, reset]);

    const isDisabledEdit = useMemo(() => {
        const status = currentPerson?.status;
        return status === "closed" || status === "closedEarly";
    }, [currentPerson]);

    const onSubmit: SubmitHandler<PersonModelInterface> = async (data) => {
        try {
            setSaving(true);

            const fullName = buildFullName(data);
            const updatedPerson: PersonModelInterface = {
                ...data,
                name: fullName,
            };

            await updatePerson(updatedPerson);
            toast.success("Пользователь сохранен!");

            // обновим локально, чтобы view показывал актуальные данные
            setCurrentPerson(updatedPerson);
            reset(updatedPerson);

            setIsEditing(false);
        } catch (error) {
            toast.error("Ошибка при сохранении пользователя!");
            console.error("Ошибка при обновлении:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        if (currentPerson) reset(currentPerson);
        setIsEditing(false);
    };

    const handleBack = () => {
        if (window.history.length > 2) navigate(-1);
        else navigate("/people");
    };

    if (isLoading) return <DetailInfoSkeleton/>;

    if (!currentPerson) {
        return (
            <div className="mx-auto w-full max-w-6xl px-4 py-6">
                <div className="rounded-xl border bg-background p-6">
                    <div className="text-lg font-semibold">Пользователь не найден</div>
                    <div className="mt-4">
                        <Button variant="outline" onClick={handleBack}>
                            <ArrowLeft className="h-4 w-4 mr-2"/>
                            Назад
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-6xl px-4 py-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <div className="text-2xl font-semibold leading-tight">{currentPerson.name}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                        Клиент с {dayjs(currentPerson.createdAt).locale("ru").format("DD.MM.YYYY")}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={handleBack}>
                        <ArrowLeft className="h-4 w-4 mr-2"/>
                        Назад
                    </Button>

                    {!isEditing ? (
                        <Button onClick={() => setIsEditing(true)} disabled={isDisabledEdit}>
                            <Edit3 className="h-4 w-4 mr-2"/>
                            Редактировать
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                                <X className="h-4 w-4 mr-2"/>
                                Отмена
                            </Button>
                            <Button onClick={handleSubmit(onSubmit)} disabled={saving}>
                                <Save className="h-4 w-4 mr-2"/>
                                {saving ? "Сохранение..." : "Сохранить"}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <Separator className="my-5"/>

            <FormProvider {...methods}>
                {/* ВАЖНО: формы ниже используют useFormContext */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* 1. Личная информация */}
                    {!isEditing ? (
                        <PersonalInfoCard isEditing={isEditing} person={currentPerson}/>
                    ) : (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium">Личная информация</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <fieldset disabled={isDisabledEdit} className="border-0 p-0 m-0">
                                    <PersonInfoForms/>
                                </fieldset>
                            </CardContent>
                        </Card>
                    )}

                    {/* 2. Информация о телефоне */}
                    {!isEditing ? (
                        <PhoneInfoCard isEditing={isEditing} person={currentPerson}/>
                    ) : (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium">Информация о телефоне</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <fieldset disabled={isDisabledEdit} className="border-0 p-0 m-0">
                                    <PhoneInfoForms/>
                                </fieldset>
                            </CardContent>
                        </Card>
                    )}

                    {/* 3. Кредит: график, выплаты, история */}
                    <CreditInfoCard
                        person={currentPerson}
                        isEditing={isEditing}
                        // если захочешь открывать попапы сверху, можно прокинуть
                        // onAddPayment={() => setIsEditing(true)}
                        // onEarlyClose={() => setIsEditing(true)}
                    />

                    {/* 4. Комментарии */}
                    <CommentsCard
                      person={currentPerson}
                      onUpdatePerson={async (next) => {
                        await updatePerson(next);
                        setCurrentPerson(next); // чтобы UI обновился сразу
                      }}
                      disabled={["closed", "closedEarly"].includes(currentPerson.status)}
                      currentUserName="Менеджер"
                    />
                </form>
            </FormProvider>
        </div>
    );
};

export default DetailInfoPage;
